/** Planos.studio types — shared active slots with 360 Tours */

import {
  type ToursSubscriptionTier as ToursSubscriptionTierBase,
  type HostingPlanId,
  TOUR_SUBSCRIPTION_TIERS as TOUR_SUBSCRIPTION_TIERS_IMPORT,
  HOSTING_PLANS as HOSTING_PLANS_IMPORT,
} from '@rehub/pricing';

export type HostingPlan = HostingPlanId;
export type HostingStatus = 'active' | 'inactive' | 'expired';
export type ToursSubscriptionTier = ToursSubscriptionTierBase;

export const TOUR_SUBSCRIPTION_TIERS = TOUR_SUBSCRIPTION_TIERS_IMPORT;
export const HOSTING_PLANS = HOSTING_PLANS_IMPORT;

export interface UserProfile {
  id: string;
  email: string;
  credits: number;
  subscription_plan?: string;
  tours_subscription_tier?: ToursSubscriptionTier | null;
  referral_code?: string;
}

export interface ReferralReward {
  id: string;
  bonus_credits: number;
  referrer_bonus_credits?: number;
  purchased_credits: number;
  created_at: string;
  referred_email: string;
  referred_name?: string;
  reward_kind?: string;
  status?: string;
}

export interface ReferralStats {
  success: boolean;
  referral_code: string;
  signup_count: number;
  friends_joined?: number;
  total_bonus_credits: number;
  credits_earned?: number;
  invite_base_url?: string;
  rewards: ReferralReward[];
}

export interface LatLng {
  lat: number;
  lng: number;
}

export type ViewMode = 'map' | 'floorplan';

export type HotspotIcon = 'camera' | 'custom' | 'pano360' | 'file' | 'video' | 'map-pin' | 'home';

export interface Hotspot {
  id: string;
  title: string;
  room_label: string;
  lat: number;
  lng: number;
  photo_url: string | null;
  heading: number; // 0-359 degrees for radar cone direction
  /** Position on floor plan (0–100), used when view_mode is 'floorplan'. */
  x_pct?: number;
  y_pct?: number;
  /** Icon type for the hotspot marker. Default 'camera'. */
  icon_type?: HotspotIcon;
  /** URL for custom icon image (used when icon_type === 'custom'). */
  icon_url?: string | null;
  /** Download link (e.g. PDF brochure) */
  download_url?: string | null;
  /** Video link (YouTube, Vimeo, etc.) */
  video_url?: string | null;
  /** Custom marker colour (hex). Falls back to viewer primary colour when unset. */
  icon_color?: string | null;
}

/** Viewer customization (colors, branding) */
export interface ViewerSettings {
  primary_color?: string;   // e.g. #FF3B30
  background_color?: string;
  accent_color?: string;
  text_color?: string;      // e.g. #ffffff — for contrast on light backgrounds
  logo_url?: string | null;
  logo_size?: 'small' | 'medium' | 'large';
  /** Optional watermark image shown behind the logo in the header (e.g. same or different image, low opacity) */
  logo_watermark_url?: string | null;
  /** Watermark opacity 0–1 (default 0.2) */
  logo_watermark_opacity?: number;
  show_powered_by?: boolean;  // default true
  /** When false, hide rooms list; show only floor plan + logo + map icon (minimal layout) */
  show_rooms_list?: boolean;  // default true
  /** When rooms list is shown: 'left' | 'right' sidebar, or 'bottom' strip. Default 'left'. */
  rooms_list_position?: 'left' | 'right' | 'bottom';
}

export interface OverlayBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** Single floor with its own floor plan image and optional map overlay bounds */
export interface PlanosFloor {
  id: string;
  label: string;
  image_url: string | null;
  overlay_bounds: OverlayBounds | null;
}

export interface PlanosProject {
  id: string;
  user_id: string;
  title: string;
  /** Internal job/reference number for quick search (e.g. PL-001). */
  internal_job_number?: string | null;
  slug?: string | null;
  address: string | null;
  google_coords: LatLng;
  floorplan_image_url: string | null;
  overlay_bounds: OverlayBounds | null;
  /** Multiple floors (Ground, First, etc.), each with its own floor plan. If empty, legacy single floor is used. */
  floors?: PlanosFloor[] | null;
  /** Map heading in degrees (0–360) to rotate the map and align with floor plan orientation */
  map_heading?: number | null;
  /** 'map' = floor plan on Google Maps; 'floorplan' = interactive floor plan only, no map */
  view_mode?: ViewMode;
  hotspots: Hotspot[];
  is_published: boolean;
  credits_spent: number;
  password?: string | null;
  hosting_plan?: HostingPlan | null;
  hosting_expires_at?: string | null;
  hosting_status?: HostingStatus;
  hosting_permanent?: boolean;
  viewer_settings?: ViewerSettings;
  /** View count from analytics (optional, may be absent until migration) */
  view_count?: number | null;
  created_at: string;
  updated_at: string;
}
